import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Plus, Star, Users, Target, Clock, Building2, Tag, RefreshCw, TrendingUp, Zap, ShieldOff, Pencil, Info } from "lucide-react";

const STATUSES = ["New", "Researching", "Ready to Contact", "Attempting Contact", "Connected",
  "Qualified", "Nurture", "Opportunity Created", "Existing Client Relationship", "Do Not Contact", "Closed / Not Interested"];
const DECISION_ROLES = ["Decision Maker", "Influencer", "Gatekeeper", "Champion", "Other"];
const CAT_COLORS = {
  "VERY HIGH": "bg-seagreen text-white border-seagreen",
  HIGH: "bg-seagreen/15 text-seagreen border-seagreen/40",
  GOOD: "bg-sky/15 text-atlantic border-sky/40",
  MODERATE: "bg-seaamber/15 text-seaamber border-seaamber/40",
  LOW: "bg-cloud text-mutedsea border-border",
};
const L = ({ label, children }) => (<label className="block"><span className="text-xs uppercase text-mutedsea tracking-wide">{label}</span><div className="mt-1">{children}</div></label>);

const VALUE_FIELDS = [["est_value_pc", "P&C"], ["est_value_benefits", "Benefits"], ["est_value_life", "Life/Key Person"],
  ["est_value_retirement", "Retirement"], ["est_value_cfp", "CFP/Wealth"], ["est_value_succession", "Succession"], ["est_value_ma", "M&A"]];

function ValueEditor({ rel, onSaved }) {
  const [form, setForm] = useState({});
  const [override, setOverride] = useState(false);
  const [reason, setReason] = useState("");
  useEffect(() => { const f = {}; VALUE_FIELDS.forEach(([k]) => f[k] = rel[k] || ""); f.actual_revenue = rel.actual_revenue || ""; f.est_value_total = rel.est_value_total || ""; setForm(f); }, [rel]);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const save = async () => {
    try {
      const body = { ...form };
      if (override) { body.override = true; body.override_reason = reason; }
      await api.put(`/boi/relationships/${rel.id}/value`, body);
      toast.success("Value saved."); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-1.5" data-testid="value-editor">
      {VALUE_FIELDS.map(([k, l]) => (
        <div key={k} className="flex items-center justify-between gap-2"><span className="text-xs text-mutedsea">{l}</span>
          <Input className="h-7 w-28 text-right" value={form[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`value-${k}`} /></div>
      ))}
      <div className="flex items-center justify-between gap-2 pt-1 border-t border-border"><span className="text-xs text-seagreen font-medium">Actual Revenue</span>
        <Input className="h-7 w-28 text-right" value={form.actual_revenue || ""} onChange={(e) => set("actual_revenue", e.target.value)} data-testid="value-actual" /></div>
      <label className="flex items-center gap-1.5 text-xs mt-1"><input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} data-testid="value-override" /> Override est. total</label>
      {override && <><Input className="h-7" placeholder="Override total" value={form.est_value_total || ""} onChange={(e) => set("est_value_total", e.target.value)} data-testid="value-override-total" /><Input className="h-7" placeholder="Reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="value-override-reason" /></>}
      <Button size="sm" className="bg-navy hover:bg-atlantic w-full mt-1" onClick={save} data-testid="value-save">Save Value</Button>
    </div>
  );
}

function ContactDialog({ open, onClose, rid, edit, onSaved }) {
  const empty = { name: "", title: "", email: "", phone: "", decision_role: "Decision Maker", is_primary: false, do_not_contact: false, linkedin_url: "" };
  const [form, setForm] = useState(empty);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  useEffect(() => { if (open) setForm(edit ? { ...empty, ...edit } : empty); }, [open, edit]);
  const save = async () => {
    if (!form.name.trim()) { toast.error("Contact name is required."); return; }
    try {
      if (edit) await api.put(`/boi/contacts/${edit.id}`, form);
      else await api.post(`/boi/relationships/${rid}/contacts`, form);
      toast.success("Contact saved."); onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="contact-dialog">
        <DialogHeader><DialogTitle className="font-serif text-navy">{edit ? "Edit Contact" : "Add Decision-Maker"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <L label="Name *"><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="contact-name" /></L>
          <L label="Title"><Input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="contact-title" /></L>
          <L label="Email"><Input value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="contact-email" /></L>
          <L label="Phone"><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="contact-phone" /></L>
          <L label="Decision Role">
            <select className="w-full h-9 border border-border rounded-sm px-2 text-sm" value={form.decision_role} onChange={(e) => set("decision_role", e.target.value)} data-testid="contact-role">
              {DECISION_ROLES.map((r) => <option key={r}>{r}</option>)}
            </select>
          </L>
          <L label="LinkedIn"><Input value={form.linkedin_url} onChange={(e) => set("linkedin_url", e.target.value)} /></L>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.is_primary} onChange={(e) => set("is_primary", e.target.checked)} data-testid="contact-primary" /> Primary contact</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.do_not_contact} onChange={(e) => set("do_not_contact", e.target.checked)} data-testid="contact-dnc" /> Do Not Contact</label>
        </div>
        <DialogFooter><Button onClick={save} className="bg-navy hover:bg-atlantic" data-testid="contact-save">Save Contact</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DetailsDialog({ open, onClose, rel, onSaved }) {
  const [form, setForm] = useState({});
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  useEffect(() => { if (open) setForm({ ...rel }); }, [open, rel]);
  const save = async () => {
    try {
      await api.put(`/boi/relationships/${rel.id}`, {
        revenue: form.revenue, employee_count: form.employee_count, locations: form.locations,
        founded_year: form.founded_year, ownership_type: form.ownership_type, industry: form.industry,
        address_state: form.address_state, known_renewal_date: form.known_renewal_date, next_follow_up: form.next_follow_up,
      });
      toast.success("Details updated — scores recalculated."); onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="details-dialog">
        <DialogHeader><DialogTitle className="font-serif text-navy">Business Details</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <L label="Industry"><Input value={form.industry || ""} onChange={(e) => set("industry", e.target.value)} data-testid="detail-industry" /></L>
          <L label="State"><Input value={form.address_state || ""} onChange={(e) => set("address_state", e.target.value)} /></L>
          <L label="Annual Revenue"><Input value={form.revenue || ""} onChange={(e) => set("revenue", e.target.value)} data-testid="detail-revenue" placeholder="12000000" /></L>
          <L label="Employee Count"><Input value={form.employee_count || ""} onChange={(e) => set("employee_count", e.target.value)} data-testid="detail-employees" /></L>
          <L label="Locations"><Input value={form.locations || ""} onChange={(e) => set("locations", e.target.value)} /></L>
          <L label="Founded Year"><Input value={form.founded_year || ""} onChange={(e) => set("founded_year", e.target.value)} /></L>
          <L label="Ownership Type"><Input value={form.ownership_type || ""} onChange={(e) => set("ownership_type", e.target.value)} placeholder="Closely Held / Family / LLC" /></L>
          <L label="Known Renewal Date"><Input type="date" value={form.known_renewal_date || ""} onChange={(e) => set("known_renewal_date", e.target.value)} /></L>
          <L label="Next Follow-Up"><Input type="date" value={form.next_follow_up || ""} onChange={(e) => set("next_follow_up", e.target.value)} /></L>
        </div>
        <DialogFooter><Button onClick={save} className="bg-navy hover:bg-atlantic" data-testid="details-save">Save & Recalculate</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ScoreDetailDialog({ open, onClose, rid, score }) {
  const [history, setHistory] = useState([]);
  useEffect(() => {
    if (open && score) api.get(`/boi/relationships/${rid}/scores/${score.business_line_id}/history`).then(({ data }) => setHistory(data.items)).catch(() => {});
  }, [open, score, rid]);
  if (!score) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="score-detail-dialog">
        <DialogHeader><DialogTitle className="font-serif text-navy">{score.business_line_name} — Score Detail</DialogTitle></DialogHeader>
        <div className="flex items-center gap-3 mb-2">
          <div className="font-serif text-4xl text-navy">{score.score}</div>
          <Badge variant="outline" className={`${CAT_COLORS[score.category_label] || ""}`}>{score.category_label}</Badge>
          <div className="text-sm text-mutedsea">Data confidence <span className="font-medium text-navy">{score.data_confidence}%</span></div>
        </div>
        <div className="text-xs text-mutedsea mb-3">ICP: {score.icp_name || "—"} ({score.icp_match != null ? Math.round(score.icp_match * 100) + "%" : "n/a"}) · rule v{score.rule_version} · calc {(score.calculated_at || "").slice(0, 16).replace("T", " ")}</div>
        <div className="space-y-1">
          {score.breakdown.map((b) => (
            <div key={b.key} className="flex items-center justify-between text-sm border-b border-border py-1" data-testid={`breakdown-${b.key}`}>
              <span className="text-slate">{b.label} <span className="text-mutedsea text-xs">— {b.reason}</span></span>
              <span className={`font-medium ${b.data_available ? "text-navy" : "text-seared"}`}>+{b.points}<span className="text-mutedsea text-xs">/{b.max}{!b.data_available ? " missing" : ""}</span></span>
            </div>
          ))}
        </div>
        {history.length > 0 && (
          <div className="mt-4">
            <div className="text-xs uppercase text-mutedsea tracking-wide mb-1">Score History</div>
            {history.map((h) => <div key={h.id} className="text-xs text-mutedsea" data-testid={`history-${h.score_version}`}>v{h.score_version} · {h.score} ({h.category_label}) · conf {h.data_confidence}% · {(h.calculated_at || "").slice(0, 10)} · {h.triggering_event}</div>)}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function RelationshipDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { can } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allLines, setAllLines] = useState([]);
  const [showContact, setShowContact] = useState(false);
  const [editContact, setEditContact] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [scoreDetail, setScoreDetail] = useState(null);
  const [recalcing, setRecalcing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/boi/relationships/${id}`); setData(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setData(false); }
    setLoading(false);
  }, [id]);
  useEffect(() => { load(); api.get("/boi/business-lines").then(({ data }) => setAllLines(data.items)); }, [load]);

  useEffect(() => {
    if (data?.relationship) api.post("/agency/recent", { resource: "business_relationships", record_id: data.relationship.id, label: data.relationship.name, party_id: data.relationship.party_id }).catch(() => {});
  }, [data?.relationship?.id]);

  const patch = async (body, msg) => {
    try { await api.put(`/boi/relationships/${id}`, body); if (msg) toast.success(msg); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleLine = (lineId) => {
    const cur = data.relationship.business_line_ids || [];
    patch({ business_line_ids: cur.includes(lineId) ? cur.filter((x) => x !== lineId) : [...cur, lineId] });
  };
  const recalc = async () => { setRecalcing(true); try { await api.post(`/boi/relationships/${id}/recalc`); toast.success("Scores recalculated."); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } setRecalcing(false); };
  const archiveContact = async (cid) => { try { await api.post(`/boi/contacts/${cid}/archive`); toast.success("Contact removed."); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  if (loading) return <div className="p-8 text-mutedsea text-sm" data-testid="br-detail-loading">Loading relationship…</div>;
  if (!data) return (
    <div className="p-8" data-testid="br-detail-notfound">
      <p className="text-mutedsea text-sm mb-3">Business relationship not found or outside your book.</p>
      <Button variant="outline" onClick={() => nav("/relationships")}><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>
    </div>
  );

  const r = data.relationship;
  const scores = data.scores || [];
  const bno = data.best_next_opportunity;
  const bna = data.best_next_action;
  const activeLineIds = r.business_line_ids || [];

  return (
    <div data-testid="br-detail-page">
      <button onClick={() => nav("/relationships")} className="text-sm text-atlantic hover:text-navy flex items-center gap-1 mb-3 nav-link" data-testid="br-back"><ArrowLeft className="w-4 h-4" />Back to relationships</button>

      <div className="bg-white border border-border rounded-sm p-5 mb-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-atlantic" /><h1 className="font-serif text-2xl text-navy" data-testid="br-detail-name">{r.name}</h1>
              {r.do_not_contact && <Badge variant="outline" className="bg-seared/10 text-seared border-seared/30 text-xs" data-testid="dnc-badge"><ShieldOff className="w-3 h-3 mr-1" />Do Not Contact</Badge>}
            </div>
            <div className="text-sm text-mutedsea mt-1">{[r.industry, [r.address_city, r.address_state].filter(Boolean).join(", "), r.revenue && `$${Number(r.revenue).toLocaleString()}`, r.employee_count && `${r.employee_count} emp`].filter(Boolean).join(" · ") || "—"}</div>
          </div>
          <div className="flex flex-col items-end gap-2">
            {can.write ? (
              <select className="h-9 border border-border rounded-sm px-2 text-sm" value={r.relationship_status} onChange={(e) => patch({ relationship_status: e.target.value }, "Status updated.")} data-testid="br-detail-status">
                {STATUSES.map((s) => <option key={s}>{s}</option>)}
              </select>
            ) : <Badge variant="outline">{r.relationship_status}</Badge>}
            {can.write && <div className="flex gap-1.5">
              <Button size="sm" variant="outline" onClick={() => setShowDetails(true)} data-testid="edit-details-btn"><Pencil className="w-3.5 h-3.5 mr-1" />Details</Button>
              <Button size="sm" variant="outline" onClick={() => patch({ do_not_contact: !r.do_not_contact }, "Suppression updated.")} data-testid="dnc-toggle">{r.do_not_contact ? "Un-suppress" : "Mark DNC"}</Button>
              <Button size="sm" variant="outline" onClick={recalc} disabled={recalcing} data-testid="recalc-btn"><RefreshCw className={`w-3.5 h-3.5 mr-1 ${recalcing ? "animate-spin" : ""}`} />Recalc</Button>
            </div>}
          </div>
        </div>
      </div>

      {/* BEST NEXT OPPORTUNITY / ACTION */}
      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <div className="bg-navy text-white rounded-sm p-4" data-testid="bno-banner">
          <div className="flex items-center gap-1.5 text-sky text-xs uppercase tracking-wide mb-1"><TrendingUp className="w-4 h-4" />Best Next Opportunity</div>
          {bno ? (<>
            <div className="flex items-baseline gap-2"><span className="font-serif text-2xl">{bno.business_line_name}</span><span className="text-sky text-lg">{bno.score}</span><Badge variant="outline" className="bg-white/10 text-white border-white/20 text-xs">{bno.category}</Badge></div>
            <ul className="text-xs text-sky/90 mt-1.5 space-y-0.5">{(bno.why || []).map((w, i) => <li key={i}>· {w}</li>)}</ul>
          </>) : <div className="text-sky/80 text-sm">Add business lines & data to compute.</div>}
        </div>
        <div className="bg-white border border-border rounded-sm p-4" data-testid="bna-banner">
          <div className="flex items-center gap-1.5 text-atlantic text-xs uppercase tracking-wide mb-1"><Zap className="w-4 h-4" />Best Next Action</div>
          {bna ? (<>
            <div className="font-serif text-2xl text-navy">{bna.action}</div>
            <ul className="text-xs text-mutedsea mt-1.5 space-y-0.5">{(bna.why || []).map((w, i) => <li key={i}>· {w}</li>)}</ul>
          </>) : <div className="text-mutedsea text-sm">—</div>}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Opportunity Scores */}
        <div className="lg:col-span-2 bg-white border border-border rounded-sm p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-serif text-lg text-navy flex items-center gap-1.5"><Target className="w-4 h-4" />Opportunity Scores</h2>
            <span className="text-xs text-mutedsea">Overall confidence {r.data_confidence_overall ?? 0}%</span>
          </div>
          <div className="space-y-2" data-testid="br-scores-panel">
            {scores.map((s) => (
              <button key={s.business_line_id} onClick={() => setScoreDetail(s)} data-testid={`score-row-${s.business_line_id}`}
                className="w-full text-left border border-border rounded-sm p-3 hover:bg-ice flex items-center gap-3">
                <div className="font-serif text-2xl text-navy w-12 text-center">{s.score}</div>
                <div className="flex-1">
                  <div className="flex items-center gap-2"><span className="font-medium text-navy">{s.business_line_name}</span><Badge variant="outline" className={`text-xs ${CAT_COLORS[s.category_label] || ""}`}>{s.category_label}</Badge>{s.opp_status && <Badge variant="outline" className="text-xs bg-ice">{s.opp_status}</Badge>}</div>
                  <div className="text-xs text-mutedsea mt-0.5">Data confidence {s.data_confidence}% · {(s.breakdown || []).slice(0, 3).map((b) => `${b.label} +${b.points}`).join(" · ")}</div>
                </div>
                <Info className="w-4 h-4 text-mutedsea" />
              </button>
            ))}
            {scores.length === 0 && <p className="text-sm text-mutedsea py-4 text-center" data-testid="no-scores">No scores yet. Add business lines and business details, then Recalc.</p>}
          </div>

          {/* Contacts */}
          <div className="flex items-center justify-between mb-3 mt-6">
            <h2 className="font-serif text-lg text-navy flex items-center gap-1.5"><Users className="w-4 h-4" />Decision-Makers ({data.contacts.length})</h2>
            {can.write && <Button size="sm" variant="outline" onClick={() => { setEditContact(null); setShowContact(true); }} data-testid="add-contact-btn"><Plus className="w-4 h-4 mr-1" />Add Contact</Button>}
          </div>
          <div className="space-y-2">
            {data.contacts.map((c) => (
              <div key={c.id} className="border border-border rounded-sm p-3 flex items-start justify-between" data-testid={`contact-${c.id}`}>
                <div>
                  <div className="flex items-center gap-2"><span className="font-medium text-navy">{c.name}</span>{c.is_primary && <Star className="w-3.5 h-3.5 text-seaamber fill-seaamber" />}<Badge variant="outline" className="text-xs">{c.decision_role}</Badge>{c.do_not_contact && <Badge variant="outline" className="text-xs bg-seared/10 text-seared border-seared/30">DNC</Badge>}</div>
                  <div className="text-xs text-mutedsea mt-0.5">{[c.title, c.email, c.phone].filter(Boolean).join(" · ") || "—"}</div>
                </div>
                {can.write && <div className="flex gap-1">
                  <button className="text-xs text-atlantic hover:underline" onClick={() => { setEditContact(c); setShowContact(true); }} data-testid={`edit-contact-${c.id}`}>Edit</button>
                  <button className="text-xs text-seared hover:underline ml-2" onClick={() => archiveContact(c.id)} data-testid={`remove-contact-${c.id}`}>Remove</button>
                </div>}
              </div>
            ))}
            {data.contacts.length === 0 && <p className="text-sm text-mutedsea py-4 text-center" data-testid="no-contacts">No decision-makers yet.</p>}
          </div>

          {/* Opportunities */}
          <div className="mt-6">
            <h2 className="font-serif text-lg text-navy flex items-center gap-1.5 mb-2"><Target className="w-4 h-4" />Opportunities ({data.opportunities.length})</h2>
            {data.opportunities.length ? (
              <div className="space-y-1.5">
                {data.opportunities.map((o) => (
                  <div key={o.id} className="border border-border rounded-sm p-2.5 text-sm flex items-center justify-between" data-testid={`br-opp-${o.id}`}>
                    <span className="text-navy">{o.name}</span><Badge variant="outline" className="text-xs">{o.stage || o.status || "—"}</Badge>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-mutedsea py-2">No opportunities linked yet.</p>}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <div className="bg-white border border-border rounded-sm p-4">
            <h2 className="font-serif text-lg text-navy mb-2">Business Lines</h2>
            <p className="text-xs text-mutedsea mb-2">Carrier-neutral — no product or carrier assigned. Each line scores independently.</p>
            <div className="flex flex-wrap gap-1.5" data-testid="br-detail-lines">
              {allLines.map((l) => (
                <button key={l.id} disabled={!can.write} onClick={() => toggleLine(l.id)} data-testid={`detail-line-${l.id}`}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${activeLineIds.includes(l.id) ? "bg-navy text-white border-navy" : "bg-white text-atlantic border-border hover:bg-ice"} ${!can.write ? "cursor-default opacity-80" : ""}`}>
                  {l.name}
                </button>
              ))}
            </div>
          </div>
          <div className="bg-white border border-border rounded-sm p-4" data-testid="value-card">
            <h2 className="font-serif text-lg text-navy mb-1 flex items-center gap-1.5"><Tag className="w-4 h-4" />Relationship Value</h2>
            <p className="text-xs text-mutedsea mb-2">All figures labelled ESTIMATE unless Actual Revenue. No personal-wealth assumptions.</p>
            {can.write ? <ValueEditor rel={r} onSaved={load} /> : (
              <div className="text-sm text-navy">Est. total: ${Number(r.est_value_total || 0).toLocaleString()} <span className="text-xs text-mutedsea">(ESTIMATE)</span><div className="text-seagreen">Actual: ${Number(r.actual_revenue || 0).toLocaleString()}</div></div>
            )}
          </div>
          <div className="bg-white border border-border rounded-sm p-4">
            <h2 className="font-serif text-lg text-navy flex items-center gap-1.5 mb-2"><Clock className="w-4 h-4" />Recent Chronology</h2>
            <div className="space-y-2">
              {data.chronology.map((a) => (
                <div key={a.id} className="text-xs border-l-2 border-sky/40 pl-2" data-testid={`br-chrono-${a.id}`}>
                  <div className="text-navy font-medium">{a.subject}</div>
                  <div className="text-mutedsea">{(a.timestamp || "").slice(0, 16).replace("T", " ")} · {a.activity_type}</div>
                </div>
              ))}
              {data.chronology.length === 0 && <p className="text-sm text-mutedsea">No activity yet.</p>}
            </div>
          </div>
        </div>
      </div>

      <ContactDialog open={showContact} onClose={() => setShowContact(false)} rid={id} edit={editContact} onSaved={load} />
      <DetailsDialog open={showDetails} onClose={() => setShowDetails(false)} rel={r} onSaved={load} />
      <ScoreDetailDialog open={!!scoreDetail} onClose={() => setScoreDetail(null)} rid={id} score={scoreDetail} />
    </div>
  );
}
